import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from '@/components/client/backend';
import { HTTPError } from 'ky';

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    ticketReference: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post('contact_submissions', {
        json: {
          name: formData.name,
          email: formData.email,
          ticket_reference: formData.ticketReference,
          message: formData.message,
          status: 'pending'
        }
      });

      toast.success("Thank you for your message! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        ticketReference: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      let description = "Please try again later";
      if (error instanceof HTTPError) {
        description = `Submission failed: ${error.response.status}`;
        // Optionally, you could try to parse the error response body
        // const errorBody = await error.response.json();
        // description = `Submission failed: ${error.response.status} - ${errorBody.message}`;
      }
      toast.error(`Failed to send message. ${description}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="relative">
          <Label
            htmlFor="name"
            className="text-white absolute -top-2.5 left-2 px-1 text-sm
                     bg-gray-800/80 transition-all duration-200
                     peer-placeholder-shown:top-3 peer-placeholder-shown:left-3
                     peer-focus:-top-2.5 peer-focus:left-2"
          >
            Name *
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="mt-1 bg-gray-900/30 border-gray-700/50 shadow-inner text-white
                     transition-all duration-200 focus:ring-2 focus:ring-purple-500/50
                     hover:border-purple-400/50 placeholder:text-gray-500"
            placeholder="Your name"
          />
        </div>

        <div className="relative">
          <Label
            htmlFor="email"
            className="text-white absolute -top-2.5 left-2 px-1 text-sm
                     bg-gray-800/80 transition-all duration-200
                     peer-placeholder-shown:top-3 peer-placeholder-shown:left-3
                     peer-focus:-top-2.5 peer-focus:left-2"
          >
            Email *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-1 bg-gray-900/30 border-gray-700/50 shadow-inner text-white
                     transition-all duration-200 focus:ring-2 focus:ring-purple-500/50
                     hover:border-purple-400/50 placeholder:text-gray-500"
            placeholder="your.email@example.com"
          />
        </div>

        <div className="relative">
          <Label
            htmlFor="ticketReference"
            className="text-white absolute -top-2.5 left-2 px-1 text-sm
                     bg-gray-800/80 transition-all duration-200
                     peer-placeholder-shown:top-3 peer-placeholder-shown:left-3
                     peer-focus:-top-2.5 peer-focus:left-2"
          >
            Ticket Reference (Optional)
          </Label>
          <Input
            id="ticketReference"
            name="ticketReference"
            value={formData.ticketReference}
            onChange={handleChange}
            className="mt-1 bg-gray-900/30 border-gray-700/50 shadow-inner text-white
                     transition-all duration-200 focus:ring-2 focus:ring-purple-500/50
                     hover:border-purple-400/50 placeholder:text-gray-500"
            placeholder="Your ticket reference number"
          />
        </div>

        <div className="relative">
          <Label
            htmlFor="message"
            className="text-white absolute -top-2.5 left-2 px-1 text-sm
                     bg-gray-800/80 transition-all duration-200
                     peer-placeholder-shown:top-3 peer-placeholder-shown:left-3
                     peer-focus:-top-2.5 peer-focus:left-2"
          >
            Message *
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            className="mt-1 min-h-[120px] bg-gray-900/30 border-gray-700/50 shadow-inner text-white
                     transition-all duration-200 focus:ring-2 focus:ring-purple-500/50
                     hover:border-purple-400/50 placeholder:text-gray-500"
            placeholder="How can we help you?"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500
                 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]
                 disabled:opacity-50 disabled:cursor-not-allowed
                 shadow-[0_0_15px_rgba(139,92,246,0.5)]
                 hover:shadow-[0_0_30px_rgba(236,72,153,0.7)]
                 animate-button-glow"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          "Send Message"
        )}
      </Button>
    </form>
  );
};
